// src/pages/transferReceiveRecord/components/TransferReceiveRecordFilters.js
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../TransferReceiveRecordSlice";
import { fetchTransferReceiveRecord } from "../TransferReceiveRecordThunk";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";

import {
  fetchEmployees,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchBranchList,
} from "../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const TransferReceiveRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.transferReceiveRecord);
  const lookups = useSelector((state) => state.lookups);

  useDefaultDateRange(setFilters, filters, "today");

  // Fetch lookup data
  useEffect(() => {
    dispatch(fetchItems());
    dispatch(fetchGroups());
    dispatch(fetchCategories());
    dispatch(fetchUsers());
    dispatch(fetchBranchList());
  }, [dispatch]);

  // Auto-run when filters change
  useEffect(() => {
    if (
      filters.filterType &&
      filters.recordType &&
      filters.fromDate &&
      filters.toDate
    ) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }
    dispatch(
      fetchTransferReceiveRecord({
        selectedFilterType: filters.filterType,
        selectedRecordType: filters.recordType,
        filters,
      })
    ).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const dynamicSpan = filters.filterType === "By Tag" ? 3 : 4;

  // Dynamic fields
  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Item":
        return (
          <Col span={dynamicSpan} flex="1 1 220px" style={{ minWidth: 200 }}>
            <label>Search Item</label>
            <SearchListAutoComplete
              entityType="item"
              optionsList={lookups.items.map((e) => ({
                sale_id: e.item_id,
                display_text: e.item_name,
              }))}
              onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
              placeholder="Item"
            />
          </Col>
        );

      case "By Group":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Group</label>
            <SearchListAutoComplete
              entityType="group"
              optionsList={lookups.groups.map((e) => ({
                sale_id: e.group_id,
                display_text: e.group_name,
              }))}
              onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)}
              placeholder="Group"
            />
          </Col>
        );

      case "By Category":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Category</label>
            <SearchListAutoComplete
              entityType="category"
              optionsList={lookups.categories.map((e) => ({
                sale_id: e.category_id,
                display_text: e.category_name,
              }))}
              onSelect={(obj) => handleFilterChange("categoryId", obj?.sale_id)}
              placeholder="Category"
            />
          </Col>
        );

      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((e) => ({
                sale_id: e.id,
                display_text: e.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
            />
          </Col>
        );

      case "By Branch":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Branch</label>
            <SearchListAutoComplete
              entityType="branch"
              optionsList={lookups.branches?.map((br) => ({
                sale_id: br.id,
                display_text: br.name,
              }))}
              onSelect={(obj) => handleFilterChange("branchId", obj?.sale_id)}
              placeholder="Branch"
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} wrap style={{ alignItems: "center" }}>
        {/* Filter Type */}
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Item", display_text: "By Item" },
              { sale_id: "By Group", display_text: "By Group" },
              { sale_id: "By Category", display_text: "By Category" },
              { sale_id: "By User", display_text: "By User" },
              { sale_id: "By Branch", display_text: "By Branch" },
            ]}
            value={{ value: filters.filterType, label: filters.filterType }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {/* Dynamic extra field */}
        {renderExtraField()}

        {/* Record Type */}
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "with item", display_text: "With Item" },
              { sale_id: "without item", display_text: "Without Item" },
            ]}
            value={{
              value: filters.recordType,
              label:
                filters.recordType === "with item"
                  ? "With Item"
                  : "Without Item",
            }}
            onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

        {/* Date Filters */}
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        {/* Report Button */}
        <Col
          xs={24} sm={12} md={9} xl={3}
          style={{ paddingTop: "1.4rem" }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default TransferReceiveRecordFilters;
